import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { brandManagerService } from "../../services/companyAuthService";
import { useLocation } from "react-router-dom";

const BrandManagers = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: ""
    },
    linkedin: "",
    department: "",
    reportingTo: "",
    employeeId: "",
    notes: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return false;
    }

    if (!formData.email.trim()) {
      toast.error("Email is required");
      return false;
    }

    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    if (!formData.phone.trim()) {
      toast.error("Phone number is required");
      return false;
    }

    if (!formData.position.trim()) {
      toast.error("Position is required");
      return false;
    }

    if (formData.linkedin && !formData.linkedin.match(/^(https?:\/\/)?(www\.)?linkedin\.com\/.+/)) {
      toast.error("Please enter a valid LinkedIn URL");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Prepare data for submission
      const submissionData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        position: formData.position.trim(),
        address: {
          street: formData.address.street?.trim() || "",
          city: formData.address.city?.trim() || "",
          state: formData.address.state?.trim() || "",
          zipCode: formData.address.zipCode?.trim() || "",
          country: formData.address.country?.trim() || ""
        },
        linkedin: formData.linkedin?.trim() || "",
        department: formData.department?.trim() || "",
        reportingTo: formData.reportingTo?.trim() || "",
        employeeId: formData.employeeId?.trim() || "",
        notes: formData.notes?.trim() || ""
      };

      const response = await brandManagerService.createBrandManager(submissionData);

      if (response.success) {
        toast.success("Brand manager added successfully!");
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          // navigate("/company/dashboard/brands/brand-managers/dashboard");
          navigate(location.state?.from || "/company/dashboard/brands/brand-managers/dashboard", {
            state: {
              newlyCreatedBrandManagerId: response.data._id
            }
          });
        }, 1200);
      } else {
        toast.error(response.message || "Failed to add brand manager");
      }
    } catch (error) {
      console.error("Error creating brand manager:", error);
      toast.error(error.message || "Failed to add brand manager");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-full bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Back Button */}
          <div className="mb-4">
            <button
              onClick={() => navigate("/company/dashboard/brands")}
              className="inline-flex items-center px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg text-[10px] md:text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200"
            >
              ← Back
            </button>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-xl shadow-md p-2 md:p-6 sm:p-8 lg:p-10">
            <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 text-center mb-6">
              Add Brand Manager's
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information Section */}
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800 mb-4">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Name */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter full name"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email address"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Position */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      Position <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      placeholder="e.g., Brand Manager, Marketing Director"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Department */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      Department
                    </label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      placeholder="Enter department"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  {/* Reporting To */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      Reporting To
                    </label>
                    <input
                      type="text"
                      name="reportingTo"
                      value={formData.reportingTo}
                      onChange={handleInputChange}
                      placeholder="Manager or supervisor name"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  {/* Employee ID */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      name="employeeId"
                      value={formData.employeeId}
                      onChange={handleInputChange}
                      placeholder="Enter employee ID"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800 mb-4">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* LinkedIn */}
                  <div className="md:col-span-2">
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleInputChange}
                      placeholder="https://www.linkedin.com/in/username"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800 mb-4">
                  Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Street */}
                  <div className="md:col-span-2">
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleInputChange}
                      placeholder="Enter street address"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  {/* City */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      placeholder="Enter city"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  {/* State */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      State/Province
                    </label>
                    <input
                      type="text"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                      placeholder="Enter state or province"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  {/* Zip Code */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      Zip/Postal Code
                    </label>
                    <input
                      type="text"
                      name="address.zipCode"
                      value={formData.address.zipCode}
                      onChange={handleInputChange}
                      placeholder="Enter zip code"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleInputChange}
                      placeholder="Enter country"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Information Section */}
              <div>
                <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800 mb-4">
                  Additional Information
                </h3>
                <div>
                  <label className="block text-[10px] md:text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Any additional notes or comments..."
                    rows="4"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-[10px] md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => navigate("/company/dashboard/brands")}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-[10px] md:text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200 w-full"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-all duration-300 font-medium text-[10px] md:text-sm w-full ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                >
                  {isSubmitting ? ("Adding...") : (
                    <>
                      <span className="sm:hidden">Add Manager</span>
                      <span className="hidden sm:inline">Add Brand Manager</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default BrandManagers;
